// What a site's two device lists say beside each row, decided (#878): where an
// access point hangs, where it is in the plan, what a switch's ports and PoE
// read, and whether Design holds changes the last apply did not carry. Every
// function is pure over reads the page already makes, so each answer is tested
// as a value and the tables only place it.
//
// **No read of its own, and none per row** (ZENITH.md §9.5). The uplink, the
// ports and the PoE come from `GET /switches/{serial}/ports/state`, one per
// switch, every switch, at most the server's `state_fan_out` at once — the
// reads the topology makes, under the same bound (#1786) — and an access
// point is never asked (ADR-0045 decision 6). The location comes from the
// site's plan and its locations, both Zenith's store. The Design fact comes
// from `GET /sites/{id}/work-list` (ADR-0117) and is **never recomputed
// here**: this reads which targets the server listed and nothing else.
//
// **An absence is said.** A switch not read, a report silent on PoE, an access
// point no switch reports, a work list the caller may not read: each is its own
// word, and none of them is rendered as zero or as "nothing".
import type { components } from "../../api/schema";
import type { Problem } from "../../api/problem";
import { plural } from "../../dashboard/format";
import { firstBy } from "./firstBy";
import { reasonWords } from "./Ports";
import { uplinkOf, type Device, type Observation, type Uplink } from "./verdicts";
import type { StateWords } from "./DevicesTable";
import { unrecognized } from "../../api/openEnum";

type WorkList = components["schemas"]["SiteWorkList"];
type PlannedDevice = components["schemas"]["PlannedDevice"];
type Location = components["schemas"]["Location"];

/** An observation's port set when the switch answered for itself, or the
 * words for why it did not: not read, still out, failed, or the switch as a
 * whole unanswered (offline, no report, no document). */
function answered(obs: Observation | undefined): { set: NonNullable<Extract<Observation, { kind: "ready" }>["set"]> } | { why: string } {
  if (!obs) return { why: "the switch was not read" };
  if (obs.kind === "unread") return { why: obs.why };
  if (obs.set.unanswered) return { why: reasonWords(obs.set.unanswered.reason) };
  return { set: obs.set };
}

/** Where an access point hangs: every read switch port whose LLDP neighbour is
 * this access point, as `switch · port`. Observation only — a declared link is
 * the topology's, beside it and never merged (ADR-0045).
 *
 * "not placed" is said only when every switch of the site answered and none
 * reports it; while one was not read, the access point may be on that one, so
 * the answer is "not read" and names how many. */
export function apUplinkOf(serial: string, switches: Device[], observed: Map<string, Observation>, name: (serial: string) => string): StateWords {
  return apUplinkFrom(serial, uplinkIndex(switches, observed, name));
}

/** What every read switch reports as its LLDP neighbours, for `apUplinkFrom`:
 * each access point's `switch · port` entries in switch order then port order,
 * the order the per-row walk found them in, and the switches not read with
 * why. Built once per observation answer (#1309), so an access point's uplink
 * is a lookup rather than a walk of every port of every switch. */
export interface UplinkIndex {
  hits: ReadonlyMap<string, string[]>;
  unread: string[];
  /** How many switches the site has, read or not. */
  switches: number;
}

export function uplinkIndex(switches: Device[], observed: Map<string, Observation>, name: (serial: string) => string): UplinkIndex {
  const hits = new Map<string, string[]>();
  const unread: string[] = [];
  for (const sw of switches) {
    const a = answered(observed.get(sw.serial));
    if ("why" in a) { unread.push(`${name(sw.serial)}: ${a.why}`); continue; }
    for (const p of a.set.ports) {
      const n = p.neighbour;
      if (!n?.in_site || n.serial === undefined) continue;
      const at = `${name(sw.serial)} · ${p.port}`;
      const have = hits.get(n.serial);
      if (have) have.push(at); else hits.set(n.serial, [at]);
    }
  }
  return { hits, unread, switches: switches.length };
}

/** `apUplinkOf` from an index the caller built once for every row: the same
 * words, decided from the same counts. */
export function apUplinkFrom(serial: string, { hits: all, unread, switches }: UplinkIndex): StateWords {
  const hits = all.get(serial) ?? [];
  if (hits.length > 0) {
    return { word: hits.join("; "), title: `The switch reports this access point as its LLDP neighbor on that port.${unread.length > 0 ? ` Not every switch was read: ${unread.join("; ")}.` : ""}` };
  }
  if (switches === 0) return { word: "not placed", title: "No switch is claimed to this site, so nothing reports where this access point is cabled." };
  if (unread.length > 0) {
    return { word: "not read", title: `No switch read reports this access point, and ${unread.length} of ${switches} ${plural(switches, "switch", "switches")} could not be read, so it may be on one of them: ${unread.join("; ")}.` };
  }
  return { word: "not placed", title: `None of the site's ${switches} ${plural(switches, "switch", "switches")} reports this access point as an LLDP neighbor.` };
}

/** Where a switch's uplink leads, from its own report (ADR-0079, ADR-0088):
 * its `wan` port and the switch of the site beyond it, or the site's way out (ADR-0150).
 * `site`, where given, is `uplinksOf`'s answer for the switch, which places it
 * under the switch whose forwarding table learned it where its own `wan` port
 * names no switch of the site (ADR-0448). */
export function switchUplinkOf(obs: Observation | undefined, isSwitch: (serial: string) => boolean, name: (serial: string) => string, site?: Uplink): StateWords {
  const u = site ?? uplinkOf(obs, isSwitch);
  // Restates ADR-0448 — learned from the other switch's forwarding table, and not announced by either.
  if (u.kind === "parent" && u.learned) return { word: `${u.port} → ${name(u.serial)}`, title: `The port this switch reaches its controller through. It reports no switch there; ${name(u.serial)}'s forwarding table learned this switch on its ${u.learned.at}, which is learned and not announced.` };
  if (u.kind === "parent") return { word: `${u.port} → ${name(u.serial)}`, title: "The port this switch reaches its controller through, and the switch of the site it reports there." };
  // The site's way out, in ADR-0150's word: a root's `wan` port is where the
  // site leaves, whatever is on the other end.
  if (u.kind === "root") return { word: u.port ? `${u.port} → WAN` : "WAN", title: "The port this switch reaches its controller through carries no switch of this site, so it is the site's WAN port." };
  return { word: "not read", title: u.why };
}

/** How many of a switch's ports have a carrier, of the ports its own document
 * declares. A port whose link field was unanswered is counted in neither, and
 * the title says how many that was. */
export function portsOf(obs: Observation | undefined): StateWords {
  const a = answered(obs);
  if ("why" in a) return { word: "not read", title: a.why };
  const ports = a.set.ports;
  const up = ports.filter((p) => p.link === "up").length;
  const silent = ports.filter((p) => p.link === undefined).length;
  return {
    word: `${up} / ${ports.length}`,
    title: `${up} of the ${ports.length} ports the switch's configuration declares have a carrier.${silent > 0 ? ` ${silent} ${plural(silent, "port")} did not say, and ${silent === 1 ? "is" : "are"} counted as neither.` : ""}`,
  };
}

/** The figures the Ports up and PoE columns sort on (#1259): how many ports
 * have a carrier, and the milliwatts the unit delivers. `undefined` where the
 * switch was not read or did not report the figure, which sorts last rather
 * than as zero (conventions/dashboards-as-code.md). */
export function portsUpCount(obs: Observation | undefined): number | undefined {
  const a = answered(obs);
  return "why" in a ? undefined : a.set.ports.filter((p) => p.link === "up").length;
}
export function poeConsumed(obs: Observation | undefined): number | undefined {
  const a = answered(obs);
  return "why" in a ? undefined : a.set.poe?.consumed_mw;
}

const watts = (mw: number) => `${(mw / 1000).toFixed(mw < 10000 ? 1 : 0)} W`;

/** The switch's PoE, from its report's unit block (#878): consumed against the
 * budget, or which of the two it did not report. Never 0 for a silent report
 * (conventions/dashboards-as-code.md). */
export function poeOf(obs: Observation | undefined): StateWords {
  const a = answered(obs);
  if ("why" in a) return { word: "not read", title: a.why };
  const p = a.set.poe;
  const used = p?.consumed_mw, budget = p?.budget_mw;
  if (used !== undefined && budget !== undefined) return { word: `${watts(used)} / ${watts(budget)}`, title: `The switch reports ${used} mW delivered against a budget of ${budget} mW, for the whole unit.` };
  if (used !== undefined) return { word: `${watts(used)} used`, title: `The switch reports ${used} mW delivered for the whole unit and no budget.` };
  if (budget !== undefined) return { word: `budget ${watts(budget)}`, title: `The switch reports a budget of ${budget} mW and not what it delivers.` };
  return { word: "not reported", title: "The switch's report carries no PoE figure for the unit." };
}

/** The site's work list as the page holds it: not asked because the caller may
 * not read it, still out, failed, or answered. */
export type WorkListRead =
  | { kind: "forbidden" }
  | { kind: "loading" }
  | { kind: "error"; problem: Problem }
  | { kind: "ready"; list: WorkList };

/** Whose apply would carry a device's configuration: the venue's for an access
 * point, the switch's own for a switch (ADR-0117 decision 1). */
export type Target = { kind: "venue" } | { kind: "switch"; serial: string };

export const WORK_LIST_PATH = "/design/changes";

/** Whether Design holds intent the last apply did not carry, for one target,
 * read from which targets the work list names — never recomputed.
 *
 * **An absent item is "nothing listed", not "up to date".** The work list
 * leaves out a target whose plan was refused as well as one whose plan
 * changes nothing (ADR-0117), and does not say which; so the word claims only
 * what the list says. A switch past the comparison's bound is "not asked", and
 * a work list the caller may not read is said as such and never as nothing. */
// Restates ADR-0117 decision 1, as amended by ADR-0347 — an item exists for a target exactly
// when its plan would change something, and the venue's also when any access point's own
// document is not the one last applied for it; a refused plan, a switch with no port intent and
// a switch past the bound produce none, which is why absence reads "nothing listed" and
// truncation "not asked". The read is `network.write`, so a caller without it is told the list
// is not theirs to read.
export function designOf(target: Target, read: WorkListRead): StateWords | undefined {
  if (read.kind === "loading") return undefined;
  if (read.kind === "forbidden") return { word: "not yours to read", title: "Whether Design has changes not yet applied comes from the site's work list, which your role cannot read at this site. Nothing is said here either way." };
  if (read.kind === "error") return { word: "not read", tone: "warn", title: `The site's work list could not be read: ${read.problem.message ?? read.problem.reason ?? "no answer"}.` };
  const list = read.list;
  if (list.cell.status === "unplaced" || list.cell.status === "unanswered") {
    return { word: "not derived", tone: "warn", title: `The work list could not be derived: the site's controller is ${list.cell.status}.` };
  }
  if (target.kind === "switch") {
    if (firstBy(list.truncated, "serial").has(target.serial)) return { word: "not asked", title: "The work list reads a limited number of switches, and this one is past that limit, so its plan was not asked for. That does not mean its plan changes nothing." };
    const item = firstBy(list.targets, "target").get(`switch:${target.serial}`);
    if (item) return { word: "changes to apply", tone: "warn", title: `Design has planned port settings for this switch that have not been applied: ${item.reasons.length} ${plural(item.reasons.length, "difference")}. Open the work list to read the plan.` };
    return { word: "nothing listed", title: "The work list names no plan to apply for this switch. A switch the site has no planned port settings for is not listed, and neither is one whose plan was refused." };
  }
  const recon = firstBy(list.targets, "kind").get("reconciliation");
  if (recon) return { word: "reconciliation to apply", tone: "warn", title: "The venue is carried, and its reconciliation is on the work list. It is approved on its own plan." };
  const venue = firstBy(list.targets, "kind").get("venue");
  if (venue) return { word: "changes to apply", tone: "warn", title: `Design has changes that have not been applied, to the site's networks or site-level settings, or to an access point's own configuration: ${venue.reasons.length} ${plural(venue.reasons.length, "difference")}. When the venue's plan is applied, every access point of the site takes it.` };
  return { word: "nothing listed", title: "The work list names no plan to apply for the site's venue. A venue whose plan was refused is not listed either; the work list's plan says why." };
}

/** What the shell's Changes to apply says for the whole site (#1811), read
 * from the same list `designOf` reads one device's word from and never
 * counted any other way: how many targets the list names, or why it names
 * none. `count` is the number of the list's items, and only an answered list
 * with none and no switch past the bound says *nothing to apply*.
 *
 * `quiet` is an answer the shell draws nothing for (#1826, #2244): an empty
 * list, and one whose only entries are plans that could not be made, which
 * the work list says and the shell does not.
 *
 * `undefined` while the read is out, and for a caller who may not read the
 * list, who is shown nothing at all. */
// Restates ADR-0117 decision 1, as amended by ADR-0347, and ADR-0104 decision 4 — an item exists
// for a target exactly when its plan would change something, so the count is the list's items; a
// refused plan produces none, which is why the empty list's title says a refused plan is not
// listed; a switch past the bound was not asked, which is never *nothing to apply*; and a
// controller that could not be read is never an empty list of work.
export function changesToApplyOf(read: WorkListRead): (StateWords & { count?: number; quiet?: true }) | undefined {
  if (read.kind === "loading" || read.kind === "forbidden") return undefined;
  if (read.kind === "error") return { word: "not read", tone: "warn", title: `Whether Design has changes to apply comes from the site's work list, which could not be read: ${read.problem.message ?? read.problem.reason ?? "no answer"}.` };
  const list = read.list;
  const status = list.cell.status;
  if (status === "unplaced" || status === "unanswered") {
    return { word: "not derived", tone: "warn", title: `The work list could not be derived: the site's controller is ${status}. This is not a site with nothing to apply.` };
  }
  if (status === "truncated") return { word: "not read", tone: "warn", title: "The work list stopped before it reached this site, so whether Design has changes to apply was not read." };
  const n = list.targets.length;
  const skipped = list.truncated.length;
  const notAsked = skipped > 0 ? ` ${skipped} ${plural(skipped, "switch", "switches")} past the limit on how many switches one work list plans ${skipped === 1 ? "was" : "were"} not asked, and may also have changes to apply.` : "";
  if (n === 0 && skipped > 0) return { word: "not asked", tone: "warn", title: `The work list names no plan that would change something among the switches it asked.${notAsked}` };
  // Restates ADR-0117 decision 1 (#2180), as Gabriel Provencher ruled on 2026-10-31 (#2244) — a
  // plan that was not made is no item and not a plan that changes nothing, so it is never said as
  // *nothing to apply*; and it is no change waiting in Design either (*"I don't understand why we
  // are seeing this message if nothing is in planning. Make it quiet."*), so the shell draws
  // nothing for it and the work list, which names the plan and why, is where it is said.
  if (n === 0 && list.not_planned.length > 0) return { word: "no change planned", quiet: true, title: "The work list names no plan that would change something. A plan it could not make is said on the work list." };
  if (n === 0) return { word: "nothing to apply", quiet: true, title: "The work list names no plan that would change something, and every plan it asked for was made." };
  const names = list.targets.map((i) => i.kind === "switch" ? (i.name || i.serial || i.target) : i.kind === "reconciliation" ? "the venue's reconciliation" : "the venue");
  const refusing = status === "unconfirmed" ? " The site's placement is unconfirmed, so applying any of them is refused until it is reconfirmed." : status === "moving" ? " The site is moving between controllers, so applying any of them is refused until the move ends." : "";
  return {
    word: "changes to apply", tone: "warn", count: n,
    title: `Design has changes no apply has carried, in ${n} ${plural(n, "plan")}: ${names.join(", ")}. Open the work list to read each plan and approve it.${refusing}${notAsked}`,
  };
}

type WorkListOutcome = components["schemas"]["WorkListOutcome"];

/** One entry of an approval, in the product's words (#1811): what the
 * work list's own apply answered for that target, and never more. */
// Restates ADR-0117 decision 3, ADR-0074 decision 2 and ADR-0223 decision 2 — each entry is that
// target's own apply; `applied` is the controller taking the configuration, which is *delivered to
// the controller* and not the device running it; `outcome_unknown` is a switch that did not answer
// in time, which is neither applied nor refused; and a refusal is never a failure.
export function outcomeOf(o: WorkListOutcome): StateWords {
  if (o.outcome === "applied") {
    return { word: "delivered to the controller", title: o.target === "venue"
      ? "The controller holds the venue's configuration, and its access points are being told to fetch it. Whether each one runs it is what it reports next."
      : "The controller holds the switch's configuration. Whether the switch runs it is what it reports next." };
  }
  if (o.outcome === "outcome_unknown") return { word: "outcome unknown", tone: "warn", title: "The controller has the configuration, and the switch did not answer in time, so it may be running it. Nothing was sent again." };
  if (o.outcome === "refused") return { word: "refused", tone: "critical", title: o.problem?.message ?? `The server answered ${o.status}.` };
  // Restates ADR-0511 decision 5a — an outcome this build does not know is shown as received, marked,
  // and read as neither delivered nor refused.
  return { word: unrecognized(o.outcome), tone: "warn", title: o.problem?.message ?? "This page does not recognize this outcome. Reload the page: a newer version may know it." };
}

/** One port Design holds intent for that the last apply did not carry: the
 * fields that differ, and what intent and the cell's held document each say
 * of them. The row is the work list's own reason, never recomputed. */
export interface NotAppliedPort {
  port: string;
  fields: { name: string; intent?: string; held?: string }[];
}

/** The ports of one device whose intent no apply has carried, read from the
 * same work-list item `designOf` reads the device's word from (#1612, #1615,
 * #1630): every reason row whose element is `<serial>/port/<name>` on the
 * switch's own `switch:<serial>` item, or for an access point on the `venue`
 * item, whose apply sends its own document (ADR-0347). **This is the one
 * source of *not applied*** for a wired port in Operate — the device page's
 * head and Wired › Ports both take it — so a count and the rows' marks cannot
 * disagree.
 *
 * `undefined` where the list has no answer for the device: not read, not
 * yours to read, not derived, a switch past the bound, or an access point at a
 * carried venue, whose venue plan was not read. An empty map is the list
 * answering and naming no port of this device, which is *nothing listed* and
 * never *applied* (ADR-0117 decision 1). */
// Restates ADR-0117 decision 1, as amended by ADR-0347 — a switch's item exists exactly when its
// plan would change something, the venue's also when an access point's own document is not the
// one last applied, and the reasons are the comparison's rows that differ; a port absent from
// them is not thereby applied, because a refused plan produces no item at all.
export function notAppliedPorts(serial: string, read: WorkListRead, family: "switch" | "ap" = "switch"): Map<string, NotAppliedPort> | undefined {
  if (read.kind !== "ready") return undefined;
  const list = read.list;
  if (list.cell.status === "unplaced" || list.cell.status === "unanswered") return undefined;
  if (family === "switch" && firstBy(list.truncated, "serial").has(serial)) return undefined;
  if (family === "ap" && firstBy(list.targets, "kind").has("reconciliation")) return undefined;
  const out = new Map<string, NotAppliedPort>();
  const item = firstBy(list.targets, "target").get(family === "switch" ? `switch:${serial}` : "venue");
  const prefix = `${serial}/port/`;
  for (const row of item?.reasons ?? []) {
    if (!row.element.startsWith(prefix)) continue;
    const port = row.element.slice(prefix.length);
    const fields = row.fields
      .filter((f) => f.disposition === "differs" && f.name !== "native_vlan")
      .map((f) => ({ name: f.name, intent: row.intent?.fields[f.name], held: row.held?.fields[f.name] }));
    out.set(port, { port, fields });
  }
  return out;
}

/** A port's not-applied fields in one sentence, intent and held named apart:
 * `untagged_vlan: planned 3001, the cell holds 1050`. */
export function notAppliedWords(p: NotAppliedPort): string {
  if (p.fields.length === 0) return "Design has planned settings for this port that have not been applied.";
  const each = p.fields.map((f) => `${f.name}: planned ${f.intent ?? "nothing"}, the controller holds ${f.held ?? "nothing"}`);
  return `Design has planned settings for this port that have not been applied: ${each.join("; ")}. What the device runs is not read here; open the work list to read the plan.`;
}

/** Where a device is in the plan: the location of the planned device bound to
 * its serial (ADR-0068 decision 3). A plan or a set of locations that could not
 * be read is said as such; still out, the answer is nothing yet. */
export function locationOf(serial: string, planned: PlannedDevice[] | "unread" | undefined, locations: Location[] | "unread" | undefined): StateWords | undefined {
  if (planned === "unread") return { word: "not read", title: "The site's plan could not be read, so where this device is placed is not known here." };
  if (!planned) return undefined;
  const pd = firstBy(planned, "serial").get(serial);
  if (!pd) return { word: "not in the plan", title: "No planned device of this site is bound to this serial." };
  if (!pd.location_id) return { word: "unassigned", title: `Bound to the planned device ${pd.name}, which is not placed at a location.` };
  if (locations === "unread") return { word: "not read", title: `Bound to ${pd.name}, placed at a location the site's locations could not be read to name.` };
  const loc = locations && firstBy(locations, "id").get(pd.location_id);
  if (!loc) return locations ? { word: "unknown location", title: `Bound to ${pd.name}, whose location this site does not list.` } : undefined;
  return { word: loc.name, title: `Bound to the planned device ${pd.name}, at ${loc.name}.` };
}
